import { useRef, useState } from 'react';
import { Box, Button, Stack, Typography, Link, IconButton, CircularProgress } from '@mui/material';
import { UploadFile, Close, InsertDriveFile } from '@mui/icons-material';
import { filesApi } from '../api/files.api';

/** Resolve a stored value to a viewable URL (handles bare paths + full http URLs). */
const toHref = (u: string): string => (/^https?:\/\//.test(u) || u.startsWith('/') ? u : `/${u}`);

interface Props {
  label?: string;
  value?: string | null;
  onChange: (url: string | null) => void;
  accept?: string;
  disabled?: boolean;
  /** Optional helper text shown under the control. */
  hint?: string;
}

/**
 * Direct document upload (replaces the old "paste a URL" text fields). Uploads the chosen file
 * to POST /files/upload and stores the returned URL via onChange. Shows view/replace/remove
 * once a file is attached.
 */
export function FileUploadField({ label, value, onChange, accept = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx', disabled, hint }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = () => inputRef.current?.click();
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const r = await filesApi.upload(file);
      onChange(r.url);
    } catch {
      setError('Upload failed — check the file type/size (max 15 MB).');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Box>
      {label && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>}
      <input ref={inputRef} type="file" accept={accept} hidden onChange={onFile} />
      {value ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <InsertDriveFile fontSize="small" color="action" />
          <Link href={toHref(value)} target="_blank" rel="noopener" variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>View uploaded file</Link>
          {!disabled && <Button size="small" onClick={pick} disabled={busy}>{busy ? 'Uploading…' : 'Replace'}</Button>}
          {!disabled && <IconButton size="small" onClick={() => onChange(null)} aria-label="remove"><Close fontSize="small" /></IconButton>}
        </Stack>
      ) : (
        <Button size="small" variant="outlined" startIcon={busy ? <CircularProgress size={14} /> : <UploadFile />} onClick={pick} disabled={busy || disabled}>
          {busy ? 'Uploading…' : 'Upload file'}
        </Button>
      )}
      {(error || hint) && <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>{error ?? hint}</Typography>}
    </Box>
  );
}
